import Axios from 'axios';
import ApiError from './ApiError';
import ERRORS from './errorConstants';
import { appConstant } from '@constants';
import { message } from 'antd';
import { APP_VARIABLES } from '@constants/app-constants';
import userManager from 'src/config/user-manager';
import { v4 as uuidv4, validate } from 'uuid';
import { COOKIE_CONSTANTS, gettingCookie, settingCookie } from 'src/helpers/cookies';

/**
 * Extracts an error message from an API response, trying
 * multiple known response shapes before falling back to a default.
 *
 * Supported shapes (in priority order):
 *   1. { data: [{ message }] }              — validation errors array
 *   2. { meta: { messages: [{ text }] } }   — standard envelope
 *   3. { error: { message } }
 *   4. { message }  /  { Message }
 *   5. Plain string body
 *   6. Provided fallback
 */
const extractApiErrorMessage = (responseData, fallback = 'Something went wrong') => {
	if (!responseData) return fallback;
	return (
		(Array.isArray(responseData?.data) && responseData.data[0]?.message) ||
		responseData?.meta?.messages?.[0]?.text ||
		responseData?.error?.message ||
		responseData?.message ||
		responseData?.Message ||
		(typeof responseData === 'string' && responseData.length <= 200 ? responseData : null) ||
		fallback
	);
};

const addDeviceIdOnHeaders = (config = {}) => {
	const cookieName = COOKIE_CONSTANTS.device.name;
	const validCookieId = gettingCookie(cookieName) && validate(gettingCookie(cookieName));
	let deviceId = validCookieId ? gettingCookie(cookieName) : null;

	if (!deviceId) {
		const newDeviceId = uuidv4();
		const cookieExpiry = COOKIE_CONSTANTS.device.expiryInDays;
		settingCookie(cookieName, newDeviceId, cookieExpiry);
		deviceId = newDeviceId;
	}
	config.headers['X-Device-Id'] = deviceId || '';
};

const setupInterceptors = (store) => {
	// Default settings for Axios request
	Axios.defaults.baseURL = appConstant.domain;
	Axios.defaults.headers.post['Content-Type'] = 'application/json';
	Axios.defaults.validateStatus = () => true;
	Axios.interceptors.request.use(
		(config) => {
			if (config.url && config.url.startsWith(process.env.REACT_APP_API_URL)) {
				config.headers.common = config.headers.common || {};
				config.headers.common['Ocp-Apim-Subscription-Key'] = process.env.REACT_APP_API_SUBSCRIPTION_KEY;
			}
			const token = localStorage.getItem(APP_VARIABLES.ACCESS_TOKEN);
			if (token) {
				const access_token = token && JSON.parse(token).access_token;
				if (config.url && config.url.includes('vimeo')) {
					config.headers.Authorization = `Bearer ${process.env.REACT_APP_VIMEO_BEARERKEY}`;
				} else {
					config.headers.Authorization = `Bearer ${access_token}`;
				}
				// Adding user browser device id on each request
				addDeviceIdOnHeaders(config);
			}
			return config;
		},
		(error) => Promise.reject(error)
	);
	Axios.interceptors.response.use(
		(response) => {
			// Process response body
			const trueString = (val) => {
				return val && typeof val === 'string' && val.length < 50;
			};
			if (response.status && parseInt(response.status, 0) > 205) {
				const resMessage = response.data
					? typeof response.data === 'object'
					: trueString(response.data.Message) === true
						? response.data.Message
						: trueString(response.data) === true
							? response.data
							: 'Something went wrong';

				switch (response.status) {
					case 500:
						message.error('Something went wrong');
						throw new ApiError(ERRORS.SERVER_ERROR);
					case 401:
						message.info('Redirecting...');
						new Promise(function (resolve, reject) {
							localStorage.removeItem(APP_VARIABLES.ACCESS_TOKEN);
							localStorage.removeItem(APP_VARIABLES.CORPORATE_ID);
							localStorage.removeItem(APP_VARIABLES.CORPORATE_NAME);
							localStorage.removeItem(APP_VARIABLES.USER_DETAILS);
							localStorage.removeItem('amount');
							localStorage.removeItem('transferFundsPopupVisible');
							localStorage.removeItem('showUnemploymentBanner');
							localStorage.clear();
							resolve();
						}).then(() => {
							userManager.signoutRedirect({ id_token_hint: localStorage.getItem(APP_VARIABLES.ID_TOKEN) });
							userManager.removeUser();
						});
						throw new ApiError(ERRORS.UNAUTHORISED_ERROR, response.data?.error?.message);
					case 403:
						new Promise(function (resolve, reject) {
							localStorage.removeItem(APP_VARIABLES.ACCESS_TOKEN);
							localStorage.removeItem(APP_VARIABLES.CORPORATE_ID);
							localStorage.removeItem(APP_VARIABLES.CORPORATE_NAME);
							localStorage.removeItem(APP_VARIABLES.USER_DETAILS);
							localStorage.removeItem('amount');
							localStorage.removeItem('transferFundsPopupVisible');
							localStorage.removeItem('showUnemploymentBanner');
							localStorage.clear();
							resolve();
						}).then(() => {
							userManager.signoutRedirect({ id_token_hint: localStorage.getItem(APP_VARIABLES.ID_TOKEN) });
							userManager.removeUser();
						});
						message.error(resMessage);
						throw new ApiError(
							ERRORS.FORBIDDEN_ERROR,
							response && response.data && response.data.Message ? response.data.Message : 'Network Error'
						);
					case 406:
						message.error(resMessage);
						throw new ApiError(ERRORS.UNAUTHORISED_ERROR, response.data.error.message);
					case 400:
						const errorMessage = extractApiErrorMessage(response?.data);
						message.error(errorMessage);
						throw new ApiError({
							...ERRORS.BAD_REQUEST_ERROR,
							message: errorMessage,
						})
					case 409:
						const conflictErrorResponse = extractApiErrorMessage(response?.data);
						message.warn(conflictErrorResponse);
						throw new ApiError({
							...ERRORS.CONFLICT_ERROR,
							message: conflictErrorResponse
						});
					case 404:
						throw new ApiError({
							...ERRORS.NOT_FOUND,
							message: extractApiErrorMessage(response?.data, ERRORS.NOT_FOUND.message),
						});
					default:
						message.error(resMessage);
						throw new ApiError(ERRORS.CLIENT_ERROR);
				}
			} else {
				return response;
			}
		},
		(error) => Promise.reject(error)
	);
};

/**
 * Set auth token as default in Axios
 * @param token
 */
export const setAuthToken = (token = localStorage.getItem(APP_VARIABLES.ACCESS_TOKEN)) => {
	Axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem(APP_VARIABLES.ACCESS_TOKEN)}`;
};
export default setupInterceptors;
